export type GroupScheduleVisibilityBucket = 'assigned' | 'adjacent' | 'browse_only';

type RawGroupScheduleCompany = {
  id: number;
  name: string;
  slug: string;
};

type RawGroupScheduleGroup = {
  id: number;
  name: string;
  slug: string;
  kind: string | null;
  parent_group_id: number | null;
};

type RawGroupScheduleVisibilitySet = {
  id: string;
  kind: string;
  bucket: GroupScheduleVisibilityBucket;
  group: RawGroupScheduleGroup;
  published_schedule: {
    status: string;
    source: string;
  };
};

type RawGroupScheduleCatalogRecord = {
  id: string;
  kind: string;
  group: RawGroupScheduleGroup;
  company: RawGroupScheduleCompany;
  visibility_bucket: GroupScheduleVisibilityBucket;
  identity_visibility: {
    mode: 'full' | 'masked' | 'hidden';
    can_view_names: boolean;
    can_view_contact_details: boolean;
    source: string;
  };
  published_schedule: {
    status: string;
    source: string;
    publication_status: string | null;
    period_identifier: string | null;
    period_start: string | null;
    period_end: string | null;
    window_start: string | null;
    window_end: string | null;
    published_at: string | null;
    last_refreshed_at: string | null;
    schedule_type: { id: number; name: string; code: string | null } | null;
    service_line: { id: number; name: string; code: string | null } | null;
    location: { id: number; name: string; code: string | null } | null;
    team: { id: number; name: string; code: string | null } | null;
  };
  affordances: {
    can_view: boolean;
    can_view_detail: boolean;
    can_browse: boolean;
    can_export: boolean;
    can_edit: boolean;
  };
  links: {
    detail: string;
  };
};

type RawGroupScheduleVisibilityContextResponse = {
  selected_company: RawGroupScheduleCompany;
  available_companies: RawGroupScheduleCompany[];
  resolved_scope: {
    key: string;
    source: string;
    label?: string | null;
    policy_id?: number | null;
  };
  default_schedule_sets: Record<GroupScheduleVisibilityBucket, RawGroupScheduleVisibilitySet[]>;
  identity_visibility: Record<
    GroupScheduleVisibilityBucket,
    {
      mode: 'full' | 'masked' | 'hidden';
      can_view_names: boolean;
      can_view_contact_details: boolean;
      source: string;
    }
  >;
  available_filters: {
    default_bucket: GroupScheduleVisibilityBucket;
    visibility_buckets: Array<{
      key: GroupScheduleVisibilityBucket;
      label: string;
      enabled: boolean;
    }>;
    groups: Array<
      RawGroupScheduleGroup & {
        bucket: GroupScheduleVisibilityBucket;
      }
    >;
  };
  policy_summary: {
    source: string;
    title: string;
    summary: string;
    notes: string[];
  };
};

type RawPublishedGroupScheduleCatalogResponse = {
  selected_company: RawGroupScheduleCompany;
  resolved_scope: {
    key: string;
    source: string;
    label?: string | null;
    policy_id?: number | null;
  };
  filters: {
    bucket: GroupScheduleVisibilityBucket | null;
    search: string | null;
    group?: string | null;
    schedule_type: number | null;
    service_line: number | null;
    location: number | null;
  };
  data: RawGroupScheduleCatalogRecord[];
  meta: {
    count: number;
    can_export: boolean;
    can_edit: boolean;
    source: string;
  };
};

export type GroupScheduleCompany = RawGroupScheduleCompany;

export type GroupScheduleGroup = {
  id: number;
  name: string;
  slug: string;
  kind: string | null;
  parentGroupId: number | null;
};

export type GroupScheduleVisibilitySet = {
  id: string;
  kind: string;
  bucket: GroupScheduleVisibilityBucket;
  group: GroupScheduleGroup;
  publishedSchedule: {
    status: string;
    source: string;
  };
};

export type GroupScheduleCatalogRecord = {
  id: string;
  kind: string;
  group: GroupScheduleGroup;
  company: GroupScheduleCompany;
  visibilityBucket: GroupScheduleVisibilityBucket;
  identityVisibility: {
    mode: 'full' | 'masked' | 'hidden';
    canViewNames: boolean;
    canViewContactDetails: boolean;
    source: string;
  };
  publishedSchedule: {
    status: string;
    source: string;
    publicationStatus: string | null;
    periodIdentifier: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    windowStart: string | null;
    windowEnd: string | null;
    publishedAt: string | null;
    lastRefreshedAt: string | null;
    scheduleType: { id: number; name: string; code: string | null } | null;
    serviceLine: { id: number; name: string; code: string | null } | null;
    location: { id: number; name: string; code: string | null } | null;
    team: { id: number; name: string; code: string | null } | null;
  };
  affordances: {
    canView: boolean;
    canViewDetail: boolean;
    canBrowse: boolean;
    canExport: boolean;
    canEdit: boolean;
  };
  links: {
    detail: string;
  };
};

export type GroupScheduleVisibilityContext = {
  selectedCompany: GroupScheduleCompany;
  availableCompanies: GroupScheduleCompany[];
  resolvedScope: {
    key: string;
    source: string;
    label?: string | null;
    policyId?: number | null;
  };
  defaultScheduleSets: Record<GroupScheduleVisibilityBucket, GroupScheduleVisibilitySet[]>;
  identityVisibility: Record<
    GroupScheduleVisibilityBucket,
    {
      mode: 'full' | 'masked' | 'hidden';
      canViewNames: boolean;
      canViewContactDetails: boolean;
      source: string;
    }
  >;
  availableFilters: {
    defaultBucket: GroupScheduleVisibilityBucket;
    visibilityBuckets: Array<{
      key: GroupScheduleVisibilityBucket;
      label: string;
      enabled: boolean;
    }>;
    groups: Array<
      GroupScheduleGroup & {
        bucket: GroupScheduleVisibilityBucket;
      }
    >;
  };
  policySummary: {
    source: string;
    title: string;
    summary: string;
    notes: string[];
  };
};

export type PublishedGroupScheduleCatalogResponse = {
  selectedCompany: GroupScheduleCompany;
  resolvedScope: {
    key: string;
    source: string;
    label?: string | null;
    policyId?: number | null;
  };
  filters: {
    bucket: GroupScheduleVisibilityBucket | null;
    search: string | null;
    scheduleType: number | null;
    serviceLine: number | null;
    location: number | null;
  };
  data: GroupScheduleCatalogRecord[];
  meta: {
    count: number;
    canExport: boolean;
    canEdit: boolean;
    source: string;
  };
};

export type GroupScheduleGridColumn = {
  key: string;
  date: string;
  label: string;
  shortLabel: string;
  isWeekend: boolean;
  isCurrentMonth: boolean;
};

export type GroupScheduleGridCell = {
  columnKey: string;
  assignmentStatus: 'assigned' | 'open' | 'not_scheduled' | 'private';
  displayName: string;
  secondaryLabel: string | null;
  privacyMode: 'full' | 'masked' | 'hidden';
  visibilityLevel: 'full' | 'summary' | 'hidden';
  source: string;
  notes: string[];
};

export type GroupScheduleGridRow = {
  id: string;
  label: string;
  timeLabel: string;
  coverageLabel: string;
  cells: GroupScheduleGridCell[];
};

export type PublishedGroupScheduleDetailResponse = {
  selectedCompany: GroupScheduleCompany;
  resolvedScope: {
    key: string;
    source: string;
    label?: string | null;
    policyId?: number | null;
  };
  data: GroupScheduleCatalogRecord & {
    scheduleGrid: {
      status: string;
      source: string;
      assignmentFactsModeled: boolean;
      representativeDemo: boolean;
      month: string;
      monthLabel: string;
      columns: GroupScheduleGridColumn[];
      rows: GroupScheduleGridRow[];
      notes: string[];
      assumptions: string[];
      followUp: string[];
    };
  };
};

const mapGroup = (group: RawGroupScheduleGroup): GroupScheduleGroup => ({
  id: group.id,
  name: group.name,
  slug: group.slug,
  kind: group.kind,
  parentGroupId: group.parent_group_id,
});

const mapVisibilitySet = (set: RawGroupScheduleVisibilitySet): GroupScheduleVisibilitySet => ({
  id: set.id,
  kind: set.kind,
  bucket: set.bucket,
  group: mapGroup(set.group),
  publishedSchedule: {
    status: set.published_schedule.status,
    source: set.published_schedule.source,
  },
});

const mapCatalogRecord = (record: RawGroupScheduleCatalogRecord): GroupScheduleCatalogRecord => ({
  id: record.id,
  kind: record.kind,
  group: mapGroup(record.group),
  company: record.company,
  visibilityBucket: record.visibility_bucket,
  identityVisibility: {
    mode: record.identity_visibility.mode,
    canViewNames: record.identity_visibility.can_view_names,
    canViewContactDetails: record.identity_visibility.can_view_contact_details,
    source: record.identity_visibility.source,
  },
  publishedSchedule: {
    status: record.published_schedule.status,
    source: record.published_schedule.source,
    publicationStatus: record.published_schedule.publication_status,
    periodIdentifier: record.published_schedule.period_identifier,
    periodStart: record.published_schedule.period_start,
    periodEnd: record.published_schedule.period_end,
    windowStart: record.published_schedule.window_start,
    windowEnd: record.published_schedule.window_end,
    publishedAt: record.published_schedule.published_at,
    lastRefreshedAt: record.published_schedule.last_refreshed_at,
    scheduleType: record.published_schedule.schedule_type,
    serviceLine: record.published_schedule.service_line,
    location: record.published_schedule.location,
    team: record.published_schedule.team,
  },
  affordances: {
    canView: record.affordances.can_view,
    canViewDetail: record.affordances.can_view_detail,
    canBrowse: record.affordances.can_browse,
    canExport: record.affordances.can_export,
    canEdit: record.affordances.can_edit,
  },
  links: record.links,
});

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? 'Your CARES session expired. Sign in again to view department schedules.'
        : response.status === 403
          ? 'You do not have access to the requested department schedule visibility scope.'
          : `Department Schedule request failed (${response.status}).`
    );
  }

  return response.json() as Promise<T>;
};

export const fetchGroupScheduleVisibilityContext = async (query: {
  company?: string;
} = {}): Promise<GroupScheduleVisibilityContext> => {
  const params = new URLSearchParams();

  if (query.company) params.set('company', query.company);

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<RawGroupScheduleVisibilityContextResponse>(
    await fetch(`/api/v1/secure/group-schedule/visibility-context${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    selectedCompany: payload.selected_company,
    availableCompanies: payload.available_companies,
    resolvedScope: {
      key: payload.resolved_scope.key,
      source: payload.resolved_scope.source,
      label: payload.resolved_scope.label,
      policyId: payload.resolved_scope.policy_id,
    },
    defaultScheduleSets: {
      assigned: payload.default_schedule_sets.assigned.map(mapVisibilitySet),
      adjacent: payload.default_schedule_sets.adjacent.map(mapVisibilitySet),
      browse_only: payload.default_schedule_sets.browse_only.map(mapVisibilitySet),
    },
    identityVisibility: {
      assigned: {
        mode: payload.identity_visibility.assigned.mode,
        canViewNames: payload.identity_visibility.assigned.can_view_names,
        canViewContactDetails: payload.identity_visibility.assigned.can_view_contact_details,
        source: payload.identity_visibility.assigned.source,
      },
      adjacent: {
        mode: payload.identity_visibility.adjacent.mode,
        canViewNames: payload.identity_visibility.adjacent.can_view_names,
        canViewContactDetails: payload.identity_visibility.adjacent.can_view_contact_details,
        source: payload.identity_visibility.adjacent.source,
      },
      browse_only: {
        mode: payload.identity_visibility.browse_only.mode,
        canViewNames: payload.identity_visibility.browse_only.can_view_names,
        canViewContactDetails: payload.identity_visibility.browse_only.can_view_contact_details,
        source: payload.identity_visibility.browse_only.source,
      },
    },
    availableFilters: {
      defaultBucket: payload.available_filters.default_bucket,
      visibilityBuckets: payload.available_filters.visibility_buckets,
      groups: payload.available_filters.groups.map((group) => ({
        ...mapGroup(group),
        bucket: group.bucket,
      })),
    },
    policySummary: payload.policy_summary,
  };
};

export const fetchPublishedGroupSchedules = async (query: {
  company?: string;
  bucket?: GroupScheduleVisibilityBucket;
  search?: string;
  scheduleType?: number;
  serviceLine?: number;
  location?: number;
  group?: string;
} = {}): Promise<PublishedGroupScheduleCatalogResponse> => {
  const params = new URLSearchParams();

  if (query.company) params.set('company', query.company);
  if (query.bucket) params.set('bucket', query.bucket);
  if (query.search) params.set('search', query.search);
  if (query.scheduleType) params.set('schedule_type', String(query.scheduleType));
  if (query.serviceLine) params.set('service_line', String(query.serviceLine));
  if (query.location) params.set('location', String(query.location));
  if (query.group) params.set('group', query.group);

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<RawPublishedGroupScheduleCatalogResponse>(
    await fetch(`/api/v1/secure/group-schedule/published-schedules${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    selectedCompany: payload.selected_company,
    resolvedScope: {
      key: payload.resolved_scope.key,
      source: payload.resolved_scope.source,
      label: payload.resolved_scope.label,
      policyId: payload.resolved_scope.policy_id,
    },
    filters: {
      bucket: payload.filters.bucket,
      search: payload.filters.search,
      scheduleType: payload.filters.schedule_type,
      serviceLine: payload.filters.service_line,
      location: payload.filters.location,
    },
    data: payload.data.map(mapCatalogRecord),
    meta: {
      count: payload.meta.count,
      canExport: payload.meta.can_export,
      canEdit: payload.meta.can_edit,
      source: payload.meta.source,
    },
  };
};

export const fetchPublishedGroupScheduleDetail = async (
  detailPath: string,
  query: {
    company?: string;
    month?: string;
    weekStart?: 'sunday' | 'monday';
  } = {}
): Promise<PublishedGroupScheduleDetailResponse> => {
  const params = new URLSearchParams();

  if (query.company) params.set('company', query.company);
  if (query.month) params.set('month', query.month);
  if (query.weekStart) params.set('week_start', query.weekStart);

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<{
    selected_company: RawGroupScheduleCompany;
    resolved_scope: {
      key: string;
      source: string;
      label?: string | null;
      policy_id?: number | null;
    };
    data: RawGroupScheduleCatalogRecord & {
      schedule_grid: {
        status: string;
        source: string;
        assignment_facts_modeled: boolean;
        representative_demo: boolean;
        month: string;
        month_label: string;
        columns: Array<{
          key: string;
          date: string;
          label: string;
          short_label: string;
          is_weekend: boolean;
          is_current_month: boolean;
        }>;
        rows: Array<{
          id: string;
          label: string;
          time_label: string;
          coverage_label: string;
          cells: Array<{
            column_key: string;
            assignment_status: 'assigned' | 'open' | 'not_scheduled' | 'private';
            display_name: string;
            secondary_label: string | null;
            privacy_mode: 'full' | 'masked' | 'hidden';
            visibility_level: 'full' | 'summary' | 'hidden';
            source: string;
            notes: string[];
          }>;
        }>;
        notes: string[];
        assumptions: string[];
        follow_up: string[];
      };
    };
  }>(
    await fetch(`${detailPath}${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    selectedCompany: payload.selected_company,
    resolvedScope: {
      key: payload.resolved_scope.key,
      source: payload.resolved_scope.source,
      label: payload.resolved_scope.label,
      policyId: payload.resolved_scope.policy_id,
    },
    data: {
      ...mapCatalogRecord(payload.data),
      scheduleGrid: {
        status: payload.data.schedule_grid.status,
        source: payload.data.schedule_grid.source,
        assignmentFactsModeled: payload.data.schedule_grid.assignment_facts_modeled,
        representativeDemo: payload.data.schedule_grid.representative_demo,
        month: payload.data.schedule_grid.month,
        monthLabel: payload.data.schedule_grid.month_label,
        columns: payload.data.schedule_grid.columns.map((column) => ({
          key: column.key,
          date: column.date,
          label: column.label,
          shortLabel: column.short_label,
          isWeekend: column.is_weekend,
          isCurrentMonth: column.is_current_month,
        })),
        rows: payload.data.schedule_grid.rows.map((row) => ({
          id: row.id,
          label: row.label,
          timeLabel: row.time_label,
          coverageLabel: row.coverage_label,
          cells: row.cells.map((cell) => ({
            columnKey: cell.column_key,
            assignmentStatus: cell.assignment_status,
            displayName: cell.display_name,
            secondaryLabel: cell.secondary_label,
            privacyMode: cell.privacy_mode,
            visibilityLevel: cell.visibility_level,
            source: cell.source,
            notes: cell.notes,
          })),
        })),
        notes: payload.data.schedule_grid.notes,
        assumptions: payload.data.schedule_grid.assumptions,
        followUp: payload.data.schedule_grid.follow_up,
      },
    },
  };
};
